import { inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AddressByGoogle, DType } from "@grupo-constant/next-dtype/types";
import { TranslocoService } from "@ngneat/transloco";
import * as lodash from 'lodash-es';
import { merge } from "lodash-es";
import { AddressListDialogComponent } from "./address-list-dialog/address-list-dialog.component";
import { CategoryTreeDialogComponent } from "./category-tree-dialog/category-tree-dialog.component";
import { ChangeWorkplaceGuideDialogComponent } from "./change-workplace-guide-dialog/change-workplace-guide-dialog.component";
import { ClipboardDataDialogComponent } from "./clipboard-data-dialog/clipboard-data-dialog.component";
import { ConceptSummaryErrorDetailsDialogComponent } from "./concept-summary-error-details-dialog/concept-summary-error-details-dialog.component";
import { ConfirmationWithCheckDialogComponent } from "./confirm-with-check-dialog/confirm-with-check-dialog.component";
import { ConfirmationWithCheckConfig } from "./confirm-with-check-dialog/confirm-with-check-dialog.types";
import { ConfirmationWithCountdownDialogComponent } from "./confirm-with-countdown-dialog/confirm-with-countdown-dialog.component";
import { ConfirmationWithCountdownConfig } from "./confirm-with-countdown-dialog/confirm-with-countdown-dialog.types";
import { DTypeMasterDataDialogComponent } from "./dtype-masterdata-dialog/dtype-masterdata-dialog.component";
import { DTypeMasterdataDialogConfig } from "./dtype-masterdata-dialog/dtype-masterdata-dialog.types";
import { EditCalendarDialogComponent } from "./edit-calendar-dialog/edit-calendar-dialog.component";
import { EditConceptDialogComponent } from "./edit-concept-dialog/edit-concept-dialog.component";
import { EditConceptSummaryDialogComponent } from "./edit-concept-summary-dialog/edit-concept-summary-dialog.component";
import { ErrorDialogComponent } from "./error-dialog/error-dialog.component";
import { ErrorDialogConfig } from "./error-dialog/error-dialog.types";
import { IconPickerDialogComponent } from "./iconpicker-dialog/iconpicker-dialog.component";
import { InfoDialogComponent } from "./info-dialog/info-dialog.component";
import { InfoDialogConfig } from "./info-dialog/info-dialog.types";
import { LocationChangeDialogComponent } from "./location-change-dialog/location-change-dialog.component";
import { PreviewFileDialogComponent } from "./preview-file-dialog/preview-file-dialog.component";
import { SyncWorkplaceDialogComponent } from "./sync-workplace-dialog/sync-workplace-dialog.component";
import { UploadExcelDialogComponent } from "./upload-excel-dialog/upload-excel-dialog.component";
import { WorkerAssignmentDialogComponent } from "./worker-assignments-dialog/worker-assignments-dialog.component";
import { WorkerHourBagDialogComponent } from "./worker-hour-bag-dialog/worker-hour-bag-dialog.component";
import { WorkerRegularizeHourBagDialogComponent } from "./worker-regularize-hour-bag-dialog/worker-regularize-hour-bag-dialog.component";
import { FuseConfirmationService } from "../../@fuse/services/confirmation/confirmation.service";
import { CategoryNode, MasterData } from "../core/master/master.types";
import { Net2BeepleLocator } from "../core/dtype/dtype.types";
import { RetrieveNet4WorkerAssignmentsRequest } from "../core/http/request.model";
import { ConceptField, ConceptSummaryErrorDetails, ConceptSummaryRow, ContractWorkSummaryRow, MonthlyContractWorkConceptSummary, Net4ConceptMapping, NextCalendar } from "../modules/admin/endpoints/endpoints.types";

@Injectable({
    providedIn: 'root'
})
export class DialogsService {

    private _matDialog = inject(MatDialog);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private translocoService = inject(TranslocoService);

    openErrorDialog(config: ErrorDialogConfig = {}): MatDialogRef<ErrorDialogComponent> {
        const _defaultConfig: ErrorDialogConfig = {
            title: this.translocoService.translate('error'),
            message: this.translocoService.translate('errorDialogMsg'),
            icon: {
                show: true,
                name: 'mat_outline:error',
                color: 'error'
            },
            actions: {
                accept: {
                    show: true,
                    label: this.translocoService.translate('accept'),
                    color: 'warn'
                },
            },
        };
        // Merge the user config with the default config
        const userConfig = lodash.merge({}, _defaultConfig, config);

        // Open the dialog
        return this._matDialog.open(ErrorDialogComponent, {
            autoFocus: false,
            disableClose: true,
            data: userConfig,
            panelClass: 'error-dialog-panel'
        });
    }

    openConfirmDialog(title: string, message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title,
            message,
            actions: {
                confirm: {
                    label: this.translocoService.translate('accept')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    openInfoDialog(config: InfoDialogConfig = {}): MatDialogRef<InfoDialogComponent> {
        const _defaultConfig: InfoDialogConfig = {
            title: this.translocoService.translate('warning'),
            message: this.translocoService.translate('warning'),
            icon: {
                show: true,
                name: 'mat_outline:info',
                color: 'primary'
            },
            actions: {
                accept: {
                    show: true,
                    label: this.translocoService.translate('accept'),
                    color: 'primary'
                },
            },
        };
        // Merge the user config with the default config
        const userConfig = lodash.merge({}, _defaultConfig, config);

        // Open the dialog
        return this._matDialog.open(InfoDialogComponent, {
            autoFocus: false,
            disableClose: true,
            data: userConfig,
            panelClass: 'info-dialog-panel'
        });
    }

    openDtypeMasterDataDialog(dType: MasterData, item?: any, readonly = false, config?: DTypeMasterdataDialogConfig): MatDialogRef<DTypeMasterDataDialogComponent> {
        const defaultConfig: DTypeMasterdataDialogConfig = {
            actions: {
                clear: {
                    key: 'clear',
                    show: true,
                    type: 'stroked',
                    color: 'primary',
                    label: this.translocoService.translate('clear'),
                    maxMr: true,
                },
                cancel: {
                    key: 'cancel',
                    show: true,
                    type: 'stroked',
                    color: 'basic',
                    label: this.translocoService.translate('cancel'),
                },
                save: {
                    key: 'save',
                    show: dType.noUpdate ? false : true,
                    type: 'flat',
                    color: 'primary',
                    label: this.translocoService.translate('save'),
                },
            }
        };
        const finalConfig: DTypeMasterdataDialogConfig = merge({}, defaultConfig, config);
        const data = { item, dType, config: finalConfig, update: !!item, readonly };
        return this._matDialog.open(DTypeMasterDataDialogComponent, {
            data,
            panelClass: 'dtype-masterdata-dialog-panel',
            width: '90vw',
            height: '80vh',
            maxWidth: '1536px',
        });
    }

    openPreviewFileDialog(file: any, width: string, height: string): MatDialogRef<PreviewFileDialogComponent, any> {
        return this._matDialog.open(PreviewFileDialogComponent, {
            data: {file},
            width: width,
            height: height
        });
    }

    openIconPickerDialog(svgIcon: string, optionNameSpaces: string[]): MatDialogRef<IconPickerDialogComponent, any> {
        return this._matDialog.open(IconPickerDialogComponent, {
            width: '70vw',
            height: '80vh',
            maxWidth: '1536px',
            panelClass: 'iconpicker-dialog-panel',
            data: {
                svgIcon,
                optionNameSpaces
            }
        });
    }

    openCategoryTreeDialog(tree: CategoryNode[], uri: string, masterName: string, listOids: string[]): MatDialogRef<CategoryTreeDialogComponent, any> {
        return this._matDialog.open(CategoryTreeDialogComponent, {
            data: {tree, uri, masterName, listOids},
            width: '60vw',
            height: '70vh'
        });
    }

    openAddressListDialog(addressList: AddressByGoogle[]): MatDialogRef<AddressListDialogComponent, any> {
        return this._matDialog.open(AddressListDialogComponent, {
            data: {
                addressList
            },
            width: '75vw',
            height: '60vh'
        });
    }

    openUploadExcelDialog(dType: DType, minHeight: string): MatDialogRef<UploadExcelDialogComponent> {
        const data = { dType };
        return this._matDialog.open(UploadExcelDialogComponent, {
            data,
            minWidth: '900px',
            maxWidth: '900px',
            minHeight: minHeight,
        });
    }

    openLocationChangeDialog(item: any): MatDialogRef<LocationChangeDialogComponent> {
        return this._matDialog.open(LocationChangeDialogComponent, {
            panelClass: 'location-change-dialog-panel',
            width: '90vw',
            height: '80vh',
            data: {
                item
            }
        });
    }

    openConfirmWithcheckDialog(config?: ConfirmationWithCheckConfig): MatDialogRef<ConfirmationWithCheckDialogComponent, any> {
        const defaultConfig: ConfirmationWithCheckConfig = {
            title: 'Confirm action',
            message: 'Are you sure you want to confirm this action?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Confirm',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Cancel'
                }
            },
            dismissible: false
        };
        const finalConfig: ConfirmationWithCheckConfig = merge({}, defaultConfig, config);
        // Open the dialog
        return this._matDialog.open(ConfirmationWithCheckDialogComponent, {
            autoFocus: false,
            disableClose: !finalConfig.dismissible,
            data: finalConfig,
            panelClass: 'confirmation-with-check-dialog-panel'
        });
    }

    openConfirmWithCountdownDialog(config?: ConfirmationWithCountdownConfig): MatDialogRef<ConfirmationWithCountdownDialogComponent, any> {
        const defaultConfig: ConfirmationWithCountdownConfig = {
            title: 'Confirm action',
            message: 'Are you sure you want to confirm this action?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            countdownMessage: 'The action will be effective in',
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: 'Confirm',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Cancel'
                }
            },
            dismissible: false
        };
        const finalConfig: ConfirmationWithCountdownConfig = merge({}, defaultConfig, config);
        // Open the dialog
        return this._matDialog.open(ConfirmationWithCountdownDialogComponent, {
            autoFocus: false,
            disableClose: !finalConfig.dismissible,
            data: finalConfig,
            panelClass: 'confirmation-with-countdown-dialog-panel'
        });
    }

    openClipboardDataDialog(clipboardItem: Clipboard) {
        return this._matDialog.open(ClipboardDataDialogComponent, {
            panelClass: 'clipboard-data-dialog-panel',
            width: '50vw',
            maxHeight: '40vh',
            data: {
                clipboardItem
            }
        })
    }

    openEditCalendarDialog(locator: Net2BeepleLocator, calendarList: NextCalendar[], readonly = false, calendar?: NextCalendar) {
        return this._matDialog.open(EditCalendarDialogComponent, {
            panelClass: 'edit-calendar-dialog-panel',
            width: '80vw',
            height: '80vh',
            data: {
                calendar,
                calendarList,
                readonly,
                locator
            }
        });
    }

    openEditConceptDialog(concept: Net4ConceptMapping, locator: Net2BeepleLocator, workers: Map<number, string>) {
        return this._matDialog.open(EditConceptDialogComponent, {
            panelClass: 'edit-concept-dialog-panel',
            width: '80vw',
            height: '80vh',
            data: {
                concept,
                locator,
                workers
            }
        });
    }

    openSyncWorkplaceDialog(beepleClientId: string, endpoint: string, workplaceCode: string, month: Date) {
        return this._matDialog.open(SyncWorkplaceDialogComponent, {
            panelClass: 'edit-concept-dialog-panel',
            width: '40vw',
            height: '40vh',
            data: {
                beepleClientId,
                endpoint,
                workplaceCode,
                month
            }
        });
    }

    openEditConceptSummaryDialog(row: ConceptSummaryRow | MonthlyContractWorkConceptSummary, concepts: ConceptField[], period: string, year: string, dayOfMonth: string, locator: Net2BeepleLocator) {
        return this._matDialog.open(EditConceptSummaryDialogComponent, {
            panelClass: 'edit-concept-summary-dialog-panel',
            width: row.summaryRow ? '80vw' : '50vw',
            height: row.summaryRow ? '80vh' : '60vh',
            data: {
                row,
                concepts,
                dayOfMonth,
                period,
                year,
                locator
            }
        })
    }

    openWorkerAssignmentDialog(request: RetrieveNet4WorkerAssignmentsRequest, workerName: string) {
        return this._matDialog.open(WorkerAssignmentDialogComponent, {
            panelClass: 'worker-assignment-dialog-panel',
            minWidth: '95vw',
            maxHeight: '60vh',
            data: {
                request,
                workerName
            }
        });
    }
    openWorkerHourBagDialog(row: ContractWorkSummaryRow, locator: Net2BeepleLocator) {
        return this._matDialog.open(WorkerHourBagDialogComponent, {
            panelClass: 'worker-hour-bag-dialog-panel',
            width: '45vw',
            maxHeight: '30vh',
            data: {
                row,
                locator
            }
        });
    }

    openWorkerRegularizeHourBagDialog(row: ContractWorkSummaryRow, locator: Net2BeepleLocator, responseWithConcepts: boolean = false) {
        return this._matDialog.open(WorkerRegularizeHourBagDialogComponent, {
            panelClass: 'worker-regularize-hour-bag-dialog-panel',
            width: '60vw',
            height: '60vh',
            minHeight: '600px',
            maxHeight: '760px',
            data: {
                row,
                locator,
                responseWithConcepts
            }
        });
    }

    openChangeWorkplaceGuideDialog(row: ContractWorkSummaryRow, locator: Net2BeepleLocator, period, year){
        return this._matDialog.open(ChangeWorkplaceGuideDialogComponent, {
            panelClass: 'change-workplace-guide-dialog-panel',
            width: '50vw',
            minWidth: '768px',
            maxHeight: '60vh',
            minHeight: '300px',

            data: {
                row,
                locator,
                period,
                year
            }
        });
    }

    openConceptSummaryErrorDetailsDialog(error:  ConceptSummaryErrorDetails) {
        return this._matDialog.open(ConceptSummaryErrorDetailsDialogComponent, {
            panelClass: 'no-padding-dialog',
            width: '70vw',
            maxHeight: '90vh',
            data: {
                error
            }
        });
    }
}
